import React, { createContext, useContext } from 'react';
import { useSchoolProfile, DEFAULT_ACADEMIC_YEAR } from '../hooks/useSchoolProfile';
import { resolveUrl as resolveLogoSrc } from '../api/client';

const FALLBACK_COLOR = '#185FA5';

// Darken a hex color by a fraction (0–1)
function darkenHex(hex, fraction) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - fraction)));
  const g = Math.max(0, Math.round(((num >> 8)  & 0xff) * (1 - fraction)));
  const b = Math.max(0, Math.round(( num        & 0xff) * (1 - fraction)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

// Convert hex to rgba string
function hexRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8)  & 0xff;
  const b =  num        & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

const SchoolBrandContext = createContext({
  color:        FALLBACK_COLOR,
  colorDark:    darkenHex(FALLBACK_COLOR, 0.35),
  colorLight:   hexRgba(FALLBACK_COLOR, 0.12),
  name:         '',
  logo:         '',
  logoSrc:      '',
  academicYear: DEFAULT_ACADEMIC_YEAR,
  loaded:       false,
});

export function SchoolBrandProvider({ children }) {
  const { profile } = useSchoolProfile();

  const color      = profile?.primary_color || FALLBACK_COLOR;
  const colorDark  = darkenHex(color, 0.35);
  const colorLight = hexRgba(color, 0.12);
  const logo       = profile?.logo_url || profile?.logo || '';
  const logoSrc    = resolveLogoSrc(logo);
  const name       = profile?.name || '';
  const academicYear = profile?.academic_year || DEFAULT_ACADEMIC_YEAR;

  return (
    <SchoolBrandContext.Provider value={{ color, colorDark, colorLight, name, logo, logoSrc, academicYear, loaded: !!profile }}>
      {children}
    </SchoolBrandContext.Provider>
  );
}

export function useSchoolBrand() {
  return useContext(SchoolBrandContext);
}

/**
 * SchoolBrandRow — compact identity strip for page headers.
 * Renders school logo + name + academic year on a colored background.
 * Only renders when at least a name or logo is available.
 */
export function SchoolBrandRow() {
  const { name, logoSrc, academicYear, loaded } = useSchoolBrand();
  const [imgErr, setImgErr] = React.useState(false);
  if (!loaded || (!name && !logoSrc)) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
      {logoSrc && !imgErr && (
        <img
          src={logoSrc}
          alt="School logo"
          onError={() => setImgErr(true)}
          style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', border: '1.5px solid rgba(255,255,255,0.4)', flexShrink: 0 }}
        />
      )}
      <div style={{ minWidth: 0 }}>
        {name && (
          <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.92)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </div>
        )}
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>{academicYear}</div>
      </div>
    </div>
  );
}
